import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getEditHardwareData, updateAsset } from '../../../../models/assetsModel';
import Modal from '../../../../components/common/Modal';

function EditHardwareModal({ isOpen, onClose, onSuccess, asset }) {
  const [formData, setFormData] = useState({
    asset_id: '',
    asset_name: '',
    asset_tag: '',
    asset_serial: '',
    asset_date_purchased: '',
    asset_description: '',
    asset_cost: '',
    asset_processor: '',
    asset_storage: '',
    asset_ram: '',
    asset_vendor_id: '',
    asset_state_id: '',
    asset_category_id: '',
    asset_ownership: '1',
    asset_commencement: '',
    asset_termination: ''
  });
  const [formOptions, setFormOptions] = useState({
    categories: [],
    vendors: [],
    states: [],
    ram: [],
    storage: [],
    processor: []
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && asset) {
      fetchEditData();
    }
  }, [isOpen, asset]);

  const fetchEditData = async () => {
    try {
      setLoading(true);
      const data = await getEditHardwareData(asset.asset_id);
      
      if (data) {
        // Set form data matching PHP field names
        setFormData({
          asset_id: asset.asset_id,
          asset_name: data.asset_name || '',
          asset_tag: data.asset_tag || '',
          asset_serial: data.asset_serial || '',
          asset_date_purchased: data.asset_date_purchased || '',
          asset_description: data.asset_description || '',
          asset_cost: data.asset_cost || '',
          asset_processor: data.asset_processor || '',
          asset_storage: data.asset_storage || '',
          asset_ram: data.asset_ram || '',
          asset_vendor_id: data.asset_vendor_id || '',
          asset_state_id: data.asset_state_id || '',
          asset_category_id: data.asset_category_id || '',
          asset_ownership: data.asset_ownership || '1',
          asset_commencement: data.asset_commencement || '',
          asset_termination: data.asset_termination || ''
        });
        
        // Set form options
        setFormOptions({
          categories: data.categories || [],
          vendors: data.vendors || [],
          states: data.states || [],
          ram: data.ram || [],
          storage: data.storage || [],
          processor: data.processor || []
        });
      }
    } catch (error) {
      console.error('Error fetching edit data:', error);
      toast.error('Failed to load asset data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.asset_name.trim()) {
      toast.error('Asset name is required');
      return;
    }
    if (!formData.asset_tag.trim()) {
      toast.error('Asset tag is required');
      return;
    }
    if (!formData.asset_serial.trim()) {
      toast.error('Asset serial number is required');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await updateAsset(formData);
      
      if (result) {
        toast.success('Hardware asset updated successfully');
        onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error('Error updating hardware asset:', error);
      toast.error('Failed to update hardware asset');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      asset_id: '',
      asset_name: '',
      asset_tag: '',
      asset_serial: '',
      asset_date_purchased: '',
      asset_description: '',
      asset_cost: '',
      asset_processor: '',
      asset_storage: '',
      asset_ram: '',
      asset_vendor_id: '',
      asset_state_id: '',
      asset_category_id: '',
      asset_ownership: '1',
      asset_commencement: '',
      asset_termination: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Edit Hardware Asset">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Hardware Asset" size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Asset Details */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Asset Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Name *
                  </label>
                  <input
                    type="text"
                    name="asset_name"
                    value={formData.asset_name}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Tag *
                    </label>
                    <input
                      type="text"
                      name="asset_tag"
                      value={formData.asset_tag}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Serial Number *
                    </label>
                    <input
                      type="text"
                      name="asset_serial"
                      value={formData.asset_serial}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Purchase *
                  </label>
                  <input
                    type="date"
                    name="asset_date_purchased"
                    value={formData.asset_date_purchased}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="asset_description"
                    value={formData.asset_description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Cost *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm">R</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      name="asset_cost"
                      value={formData.asset_cost}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Processor
                    </label>
                    <select
                      name="asset_processor"
                      value={formData.asset_processor}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Processor</option>
                      {formOptions.processor.map((processor) => (
                        <option key={processor.category_id} value={processor.category_id}>
                          {processor.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Storage/Size
                    </label>
                    <select
                      name="asset_storage"
                      value={formData.asset_storage}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Storage</option>
                      {formOptions.storage.map((storage) => (
                        <option key={storage.category_id} value={storage.category_id}>
                          {storage.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RAM
                    </label>
                    <select
                      name="asset_ram"
                      value={formData.asset_ram}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select RAM</option>
                      {formOptions.ram.map((ram) => (
                        <option key={ram.category_id} value={ram.category_id}>
                          {ram.category_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Categories and Ownership */}
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Classification</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Vendor *
                  </label>
                  <select
                    name="asset_vendor_id"
                    value={formData.asset_vendor_id}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Vendor</option>
                    {formOptions.vendors.map((vendor) => (
                      <option key={vendor.category_id} value={vendor.category_id}>
                        {vendor.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Condition *
                  </label>
                  <select
                    name="asset_state_id"
                    value={formData.asset_state_id}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Condition</option>
                    {formOptions.states.map((state) => (
                      <option key={state.category_id} value={state.category_id}>
                        {state.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Category
                  </label>
                  <select
                    name="asset_category_id"
                    value={formData.asset_category_id}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Category</option>
                    {formOptions.categories.map((category) => (
                      <option key={category.category_id} value={category.category_id}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Ownership
                  </label>
                  <select
                    name="asset_ownership"
                    value={formData.asset_ownership}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="1">Owned</option>
                    <option value="2">Rental</option>
                  </select>
                </div>

                {/* Rental dates - show only when ownership is rental */}
                {formData.asset_ownership === '2' && (
                  <div className="space-y-3 pt-2 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Commencement Date
                      </label>
                      <input
                        type="date"
                        name="asset_commencement"
                        value={formData.asset_commencement}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Termination Date
                      </label>
                      <input
                        type="date"
                        name="asset_termination"
                        value={formData.asset_termination}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isSubmitting ? 'Updating...' : 'Update Asset'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default EditHardwareModal;
